import { useTitle } from "@fyendalscollection/marketing/lib/useTitle";

export const PagePrivacyPolicy = (): JSX.Element => {
	useTitle("Privacy Policy");

	return (
		<>
			<h1>Privacy Policy</h1>
			<p>
				This privacy policy sets out the way that Fyendal&apos;s Collection
				(<b>we</b>, <b>us</b>, or <b>our</b>) complies with the
				requirements of privacy laws, codes and regulations in New
				Zealand (Privacy Laws), and governs how we collect, use and
				protect any personal information you provide to us, whether
				online, via phone, text, email, in letters or in other
				correspondence.
			</p>
			<p>
				This privacy policy explains:
			</p>
			<ul>
				<li>
					what information we may collect about you and how we store
					that information;
				</li>
				<li>
					how we will use and store information we collect about you;
				</li>
				<li>
					when we may use your details to contact you;
				</li>
				<li>
					whether we will disclose your details to anyone else;
				</li>
				<li>
					your choices regarding the personal information you provide
					to us; and
				</li>
				<li>
					the use of cookies on our website and how you can exercise
					your choices in relation to those cookies.
				</li>
			</ul>
			<p>
				Please note that New Zealand&apos;s Privacy Laws may differ from other
				country&apos;s privacy laws.
			</p>

			<h2 id="informationWeCollect">The information we collect</h2>
			<p>
				We may collect information about you (such as your name and
				email address, etc.) through:
			</p>
			<ul>
				<li>
					you contacting us directly;
				</li>
				<li>
					providing goods or services to you (where relevant);
				</li>
				<li>
					you filling out a form on our website, including a
					&apos;Contact Us&apos; form.
				</li>
				<li>
					you engaging with the Fyendal&apos;s Collection team or
					community on Discord.
				</li>
			</ul>
			<p>
				If you provide us with personal information about another
				person, you should obtain their consent to do so, having
				informed them of this privacy policy where appropriate.
			</p>
			<p>
				We automatically collect certain non-personally identifiable
				information when you visit our website (such as the type of
				browser you are using, the type of operating system you are
				using, and the domain name of your internet service provider).
			</p>

			<h2 id="howWeUseThisInformation">How we use this information</h2>
			<p>
				We will use your personal information for a number of purposes
				including the following:
			</p>
			<ul>
				<li>
					in relation to any correspondence you have entered into with
					us;
				</li>
				<li>
					to provide any goods or services to you (where relevant);
				</li>
				<li>
					for internal administration purposes;
				</li>
				<li>
					for marketing purposes where you have specifically consented
					to receive marketing communications from us (see below);
				</li>
				<li>
					to provide you with information about our activities or
					online content you have agreed to receive;
				</li>
				<li>
					to personalise the way our content is presented to you;
				</li>
				<li>
					to use IP addresses to identify the location of users, to
					block disruptive use, to establish the number of visits from
					different countries.
				</li>
			</ul>
			<p>
				We use non-personally identifiable information to analyse usage
				of our website (such as aggregated information on the pages
				visited by our users), which allows us to improve the design and
				content of our site.  Any data gathered will be shared, if
				appropriate, with any third parties who provide services to us
				or on our behalf.
			</p>

			<h2 id="contactedForMarketingPurposes">
				Will I be contacted for marketing purposes?
			</h2>
			<p>
				We will only contact you for marketing purposes where you have
				provided us with your consent to do so.  You can opt out of
				receiving electronic marketing messages at any time by
				contacting us, or by using the unsubscribe facility contained
				in the relevant messages.
			</p>

			<h2 id="sharingAndDisclosureOfPersonalInformation">
				Sharing and disclosure of personal information
			</h2>
			<p>
				We will only use your information for the purposes for which it
				was obtained.  We may share your personal information with any
				of the following: our professional advisors, legal tribunals and
				courts, Stripe Inc., Amplitude Inc., Amazon Web Services Inc.,
				DataDog Inc. and any of our third party service providers (including web
				services and cloud storage/hosting providers) where that
				disclosure is reasonably required to fulfil any purpose for
				which your personal information was collected.
			</p>
			<p>
				We may also disclose personal information where:
			</p>
			<ul>
				<li>we are required to do so by law;</li>
				<li>
					you have expressly consented to the disclosure or the
					consent may be reasonably inferred from the circumstances;
					or
				</li>
				<li>
					we are otherwise permitted to do so under Privacy Laws.
				</li>
			</ul>
			<p>
				For the purposes set out above we may transfer personal
				information to parties located in other countries (some of which
				may not have an equivalent level of data protection laws as
				those in New Zealand).  Before transferring any personal
				information to an overseas recipient, we take steps that are
				reasonable in the circumstances to ensure the overseas recipient
				complies with the principles contained in New Zealand Privacy
				Laws or is bound by a substantially similar privacy scheme
				unless you consent to the disclosure or it is otherwise required
				or permitted by law.
			</p>

			<h2 id="securityOfInformation">Security of information</h2>
			<p>
				We hold personal information in accordance with Privacy Laws.
			</p>
			<p>
				We are committed to keeping personal information secure. We will
				use all reasonable precautions to protect personal information
				from misuse, interference and loss, as well as unauthorised
				access, modification or disclosure.
			</p>
			<p>
				We cannot guarantee the privacy of personal information that you
				transmit over the internet or that may be collectable in transit
				by others (including contractors who provide services to us).
			</p>

			<h2 id="accessYourPersonalInformation">
				How you can access or correct your personal information
			</h2>
			<p>
				You have a right to request access to, and correction of,
				personal information we hold about you by contacting us at the
				details in the &apos;Contact Us&apos; section below. Your request will be
				dealt with in accordance with Privacy Laws.
			</p>
			<p>
				The accuracy of the information we hold about you is important.
				Please advise us if your personal information changes to help in
				ensuring that the information we hold is up to date, complete
				and relevant.
			</p>

			<h2 id="collectionOfInformationByOthers">
				Collection of information by others
			</h2>
			<p>
				Our Website contains links to other sites and you should consult
				the other sites&apos; privacy notices.  Their information practices
				may be different from ours and we have no control over
				information that is submitted to, or collected by, these third
				parties. We cannot accept any responsibility or liability for
				the privacy practices of third party websites and your use of
				those websites is at your own risk.
			</p>

			<h2 id="cookies">Cookies</h2>
			<p>
				Cookies are small text files on your device.  They are made by
				your web-browser when you visit a website.  Every time you go
				back to that website, your browser will send the cookie file
				back to our website&apos;s server.
			</p>
			<p>
				We may use cookies on our website to help us remember the
				information you enter, by passing a unique identification
				between your device and the website that identifies you.  This
				means that as you use the website we can relay any information
				you have entered earlier back to you at a later time.
			</p>
			<p>
				Other cookies and authentication tokens may be used to help us
				tailor services or to deliver information to you that may be
				more relevant or interesting to you.  We may use automated
				devices and applications to evaluate usage of our website. We
				use these tools to help us improve our website&apos;s performance and
				user experiences.
			</p>
			<p>
				Where cookies hold any personal information, those cookies will
				be handled in accordance with this privacy policy.
			</p>
			<p>
				If you choose to disable or block cookies, parts of our website
				may not function as intended, or at all.  Cookies can be
				disabled or blocked through your browser settings.
			</p>

			<h2 id="changesToOurPrivacyPolicy">
				Changes to our Privacy Policy
			</h2>
			<p>
				We reserve the right to revise our privacy policy, or any part
				of it, from time to time.  Please review the policy periodically
				for changes.
			</p>

			<h2 id="contactUs">Contact us</h2>
			<p>
				If you have any questions or concerns about this privacy policy
				please contact us on the Fyendal&apos;s Collection Discord.
				If you decide that we should no longer hold any of
				your personal data, or wish to change how your data may be used,
				please contact us.
			</p>
		</>
	);
};
